import React, { useEffect, useRef, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { propOr, set } from 'ramda';
import { useLocation, useParams } from 'react-router';
import { determineLocale, getLocale } from '../utils/determineLocale';
import { HPX_SUPPORTED_LANGUAGES_KEY, RTL_LANG } from '../constants/i18n';
import {
  ReTargetContainer,
  ReTargetContentContainer,
  ConsentsButtonContainer,
  Title,
  Body,
  ToggleLayout,
  Footer,
  StyledAccordion,
  ToggleLabelText,
  ToggleLabelHeader,
  Graphic,
  WjaContainer,
  WjaToggleHeader,
  WjaToggleHeaderTitle,
  WjaToggleBody,
  WjaToggleBodyExtens,
  WjaToggleBodyExtensContainer,
  WjaToggleBodyExtensContainerDesc,
  WjaToggleBodyExtensContainerButton,
  WjaToggleBodyExtensContainerButtonText,
  RebrandAccordionHeaderContent,
  RebrandToggleLabelHeader,
  RebrandToggleLabelText,
  RebrandAccordionHeaderStart,
  RebrandCardContent,
  RebrandBackGround,
  RebrandEmptyPageWrapper,
  RebrandEmptyPageContentWrapper,
  RebrandUserConsentsTitle,
  RebrandUserConsentsBody,
  RebrandUserConsentsUpdateButtonContainer,
  RebrandUserConsentsCardContent,
  ListContainerRebrand,
  ListContainerTitleRebrand,
  ListContainerContentToggleRebrand,
  PrinterConsentDescription,
  RebrandUserConsentsUpdateButtonLargeScreen,
  ConsentsButtonLargeContainer,
  ButtonLoaderWrapper
} from '../styles/privacySettingsStylesRebrand';
import IconShield from '@veneer/core/dist/scripts/icons/icon_shield';
import svgAnalytics from '../assets/analytics.svg';
import { IOS, ANDROID } from '../constants/purposeID';
import { renderTrackEvent } from '../lib/analyticsTracking';
import {
  IconJobs,
  IconMegaphone,
  IconChatText,
  IconBarChart,
  IconShare,
  Button,
  Toggle as UiToggle,
  Accordion,
  EmptyCard,
  DeviceListPage,
  ListContainer,
  EmptyPage,
  Toggle,
  GenericCard,
  Loader,
} from '@clientos/ui-toolkit';
import useIsSmallScreen from '../utils/useIsSmallScreen';
import {
  ACTION_AUX_PARAMS,
  ACTIONS,
  CONTROL_NAMES,
  HYPERLINKS_HREFS,
  HYPERLINKS_URLS_ENDING_PATHS,
  SCREEN_NAMES,
  SCREEN_PATHS
} from '../jweb/constants';
import {
  AppendProcessedParamsToEvent,
  CreateAnalyticsControlEvent,
  CreateAnalyticsScreenEvent,
  ProcessAuxParams
} from '../utils/simpleUIAnalytics';
import { sendSimpleUiEvent } from '../jweb/plugins/dataCollection';
import PrinterConsentsToggle from '../components/PrinterConsentsToggles';

const PrivacySettingsRebrand = ({
  handleClick,
  backClick,
  analytics = false,
  advertising = false,
  dataTransfer = false,
  setAnalytics,
  setAdvertising,
  setDataTransfer,
  csData = {},
  loading,
  appAnalyticsContent = '',
  advertisingContent = '',
  personalizedSuggestionsContent = '',
  dataTransferContent = '',
  smallButton,
  query,
  printerWelcome,
  seteasyStartProOperation,
  easyStartProUsage = false,
  seteasyStartProUsage,
  easyStartProOperationContent = '',
  easyStartProUsageContent = '',
  defaultConsentContent = '',
  seteasyStartBasic,
  easyStartUsage = false,
  seteasyStartUsage,
  easyStartBasicContent = '',
  easyStartUsageContent = '',
  didUserConsentLoad
}) => {
  const { pathname, search } = useLocation();
  const { platform } = useParams();
  const userConsents = pathname.includes('user-consents');
  const printerConsentsUpdate = pathname.includes('printer-consents-update');
  const userConsentsUpdate = pathname.includes('user-consents-update');
  const onBoard = search.includes('onBoard=true');
  const title = propOr('', 'title', csData);
  const body = propOr('', 'body', csData);
  const footer = propOr('', 'footer', csData);
  const appRequiredTitle = propOr('', 'requiredTitle', csData);
  const appOptionalTitle = propOr('', 'optionalTitle', csData);
  const appOptionalDataUseDesc = propOr('', 'optionalDataUseDesc', csData);
  const appAnalyticsTitle = propOr('', 'analyticsTitle', csData);
  const advertisingTitle = propOr('', 'advertisingTitle', csData);
  const appDataTransferTitle = propOr('', 'dataTransferTitle', csData);
  const easyStartProRequiredText = propOr('', 'required', csData);
  const easyStartProAnalyticsText = propOr('', 'analyticsTitle', csData);
  const easyStartRequiredText = propOr('', 'required', csData);
  const easyStartAnalyticsText = propOr('', 'analyticsTitle', csData);
  const continueButton = propOr('', 'continueButton', csData);
  const backButton = propOr('', 'backButton', csData);
  const saveButton = propOr('', 'saveButton', csData);
  const isDesktop = platform === IOS || platform === ANDROID ? false : true;
  const isIOS = platform === IOS ? true : false;
  const queryParams = new URLSearchParams(search);
  const isShowBackButton = queryParams.get('secondaryButton') !== 'none';
  const { language } = getLocale(HPX_SUPPORTED_LANGUAGES_KEY);
  const { country } = determineLocale(HPX_SUPPORTED_LANGUAGES_KEY);
  const isRTL = RTL_LANG.includes(language) ? true : false;
  const queryParameter = query.appName || '';
  const hasAppNameEasyStart = queryParameter.toLowerCase() === 'easystart';
  const hasAppNameEasyStartPro = queryParameter.toLowerCase() === 'easystartpro';
  const [isRenderContinueButton, setRenderContinueButton] = useState(false);
  const [isRenderPageAnyToggleClick, setRenderPageAnyToggleClick] = useState(false);
  const [isEnableContinueButton, setEnableContinueButton] = useState(false);
  const { ffAnalyticsTracking } = useFlags();
  const analyticsRef = useRef(analytics);
  const advertisingRef = useRef(advertising);
  const dataTransferRef = useRef(dataTransfer);
  const [didConsentsSyncBeforeHandleButtonClick, setDidConsentsSyncBeforeHandleButtonClick] =
    useState(false);
  const printerConsents = pathname.includes('printer-consents');
  const isWorkFromHomePrinter = query && query.wfh && query.wfh === 'true';
  const isPipl = search.includes('isPipl=true');
  const buttonsContainerRef = useRef(null);
  const [a11yAnnouncement, setA11yAnnouncement] = useState('');

  const queryParam = query.appName || '';
  const hasAppNameWJA = queryParam.toLowerCase() === 'wja';

  const handleAnalyticsChange = (e) => {
    analyticsRef.current = e;
  };
  const handleAdvertisingChange = (e) => {
    advertisingRef.current = e;
  };
  const handleDataTransferChange = (e) => {
    dataTransferRef.current = e;
  };

  const handleToggleChangeWithFocus = (toggleId, stateSetter, value, additionalActions) => {
    const input = document.getElementById(`${toggleId}__toggle`);
    if (input) {
      input.setAttribute('aria-checked', String(value));
      const label = input.getAttribute('aria-label') || '';
      // aria-live="polite" announces after VoiceOver finishes current speech,
      // without moving the VoiceOver cursor away from the toggle.
      if (label) {
        setA11yAnnouncement(`${label} ${value ? 'on' : 'off'}`);
        // Clear after announcement so stale text is not re-read on next render.
        setTimeout(() => setA11yAnnouncement(''), 1000);
      }
    }
    stateSetter(value);
    if (additionalActions) {
      additionalActions();
    }
  };

  function useMediaQuery(query) {
    const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

    useEffect(() => {
      const media = window.matchMedia(query);
      const listener = () => setMatches(media.matches);
      media.addEventListener('change', listener);
      return () => media.removeEventListener('change', listener);
    }, [query]);

    return matches;
  }
  const isLargeScreen = useMediaQuery('(min-width: 768px)');

  useEffect(() => {
    if (didConsentsSyncBeforeHandleButtonClick) {
      handleClick();
      continueClickBatchEvent();
    }
    setDidConsentsSyncBeforeHandleButtonClick(false);
  }, [didConsentsSyncBeforeHandleButtonClick]);

  const printerActionAuxParams = {
    source: ACTION_AUX_PARAMS.SOURCE.HP_SMART_APP,
    flow: isWorkFromHomePrinter
      ? ACTION_AUX_PARAMS.FLOW.WFH
      : ACTION_AUX_PARAMS.FLOW.DEVICE_ONBOARDING
  };
  const printerProcessedActionAuxParams = ProcessAuxParams(printerActionAuxParams);

  const defaultTrackEventParams = {
    screenPath: userConsents
      ? SCREEN_PATHS.REACT_PRIVACY_CONSENTS_APP_WELCOME
      : SCREEN_PATHS.REACT_PRIVACY_CONSENTS_PRINTER_CONSENT,
    screenName: userConsents
      ? SCREEN_NAMES.APP_MANAGE_OPTIONS
      : SCREEN_NAMES.PRINTER_MANAGE_OPTIONS,
    ...(printerConsents ? { actionAuxParams: printerProcessedActionAuxParams } : {})
  };

  const managePrivacyScreenTrackEvent = CreateAnalyticsScreenEvent({
    ...defaultTrackEventParams,
    action: ACTIONS.SCREEN_DISPLAYED
  });

  const continueButtonDisplayTrackEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    action: ACTIONS.CONTROL_DISPLAYED,
    controlName: CONTROL_NAMES.CONTINUE
  });

  const continueButtonClickTrackEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    action: ACTIONS.CONTROL_BUTTON_CLICKED,
    controlName: CONTROL_NAMES.CONTINUE
  });

  const backButtonClickTrackEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    action: ACTIONS.CONTROL_BUTTON_CLICKED,
    controlName: CONTROL_NAMES.BACK
  });

  const analyticsToggleTrackEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    action: analytics ? ACTIONS.CONTROL_TOGGLE_ENABLED : ACTIONS.CONTROL_TOGGLE_DISABLED,
    controlName: !!userConsentsUpdate
      ? CONTROL_NAMES.APP_ANALYTICS
      : CONTROL_NAMES.PRINTER_ANALYTICS
  });

  const advertisingToggleTrackEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    action: advertising ? ACTIONS.CONTROL_TOGGLE_ENABLED : ACTIONS.CONTROL_TOGGLE_DISABLED,
    controlName: !!userConsentsUpdate
      ? CONTROL_NAMES.APP_ADVERTISING
      : CONTROL_NAMES.PRINTER_ADVERTISING
  });

  const dataTransferToggleTrackEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    action: dataTransfer ? ACTIONS.CONTROL_TOGGLE_ENABLED : ACTIONS.CONTROL_TOGGLE_DISABLED,
    controlName: CONTROL_NAMES.DATA_TRANSFER
  });

  const hpAITermsOfUseHyperLinkClickEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
    controlName: CONTROL_NAMES.AI_TERMS_OF_USE
  });

  const termsOfUseHyperLinkClickEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
    controlName: CONTROL_NAMES.TERMS_OF_USE
  });

  const eulaHyperLinkClickEvent = CreateAnalyticsControlEvent({
    ...defaultTrackEventParams,
    action: ACTIONS.CONTROL_HYPERLINK_CLICKED,
    controlName: CONTROL_NAMES.EULA
  });

  const continueClickBatchEvent = () => {
    country === 'cn' && printerConsentsUpdate && sendSimpleUiEvent(dataTransferToggleTrackEvent);
    sendSimpleUiEvent(analyticsToggleTrackEvent);
    sendSimpleUiEvent(advertisingToggleTrackEvent);
    sendSimpleUiEvent(continueButtonClickTrackEvent);
  };

  useEffect(() => {
    if (csData !== null) {
      const hyperlinkElements = document.querySelectorAll('a');
      if (hyperlinkElements) {
        hyperlinkElements.forEach((link) => {
          if (
            link.href === HYPERLINKS_HREFS.TERMS_OF_USE ||
            link.href.split('/').at(-1) === HYPERLINKS_URLS_ENDING_PATHS.TERMS_OF_USE
          ) {
            AppendProcessedParamsToEvent(
              termsOfUseHyperLinkClickEvent,
              ProcessAuxParams({ url: link.href })
            );
            link.addEventListener('click', () => {
              sendSimpleUiEvent(termsOfUseHyperLinkClickEvent);
            });
          } else if (
            link.href === HYPERLINKS_HREFS.AI_TERMS_OF_USE ||
            link.href.split('/').at(-1) === HYPERLINKS_URLS_ENDING_PATHS.AI_TERMS_OF_USE
          ) {
            AppendProcessedParamsToEvent(
              hpAITermsOfUseHyperLinkClickEvent,
              ProcessAuxParams({ url: link.href })
            );
            link.addEventListener('click', () => {
              sendSimpleUiEvent(hpAITermsOfUseHyperLinkClickEvent);
            });
          } else if (
            link.href === HYPERLINKS_HREFS.EULA ||
            link.href.split('/').at(-1) === HYPERLINKS_URLS_ENDING_PATHS.EULA ||
            link.href.split('/').at(-1) === HYPERLINKS_URLS_ENDING_PATHS.US_EULA
          ) {
            AppendProcessedParamsToEvent(
              eulaHyperLinkClickEvent,
              ProcessAuxParams({ url: link.href })
            );
            link.addEventListener('click', () => {
              sendSimpleUiEvent(eulaHyperLinkClickEvent);
            });
          }
        });
      }
    }
    if (userConsents && onBoard && !isRenderContinueButton && csData !== null) {
      renderTrackEvent(ffAnalyticsTracking);
      setRenderContinueButton(true);
    }
    if (csData !== null && !isRenderContinueButton && onBoard) {
      setRenderContinueButton(true);
    }
    if (buttonsContainerRef.current) {
      const pageContentElement = document.querySelector('.clientos-ui-toolkit-empty-page .clientos-ui-toolkit-empty-page__content')
      if (pageContentElement) {
        pageContentElement.style.paddingBottom = `${buttonsContainerRef.current.offsetHeight}px`;
      }
    }
  }, [csData]);

  useEffect(() => {
    if (userConsents && isRenderPageAnyToggleClick && csData !== null) {
      setEnableContinueButton(true);
    }
  }, [isRenderPageAnyToggleClick]);

  useEffect(() => {
    if (isRenderContinueButton) {
      sendSimpleUiEvent(continueButtonDisplayTrackEvent);
    }
  }, [isRenderContinueButton]);

  useEffect(() => {
    sendSimpleUiEvent(managePrivacyScreenTrackEvent);
  }, []);

  useEffect(() => {
    const configs = [
      { id: 'dataTransfer-toggle', label: appDataTransferTitle },
      { id: 'app-analytics-toggle', label: appAnalyticsTitle },
      { id: 'advertising-toggle', label: advertisingTitle },
      { id: 'app-data-transfer-toggle', label: appDataTransferTitle },
      { id: 'app-advertising-toggle', label: advertisingTitle },
    ];
    configs.forEach(({ id, label }) => {
      const input = document.getElementById(`${id}__toggle`);
      if (input && label) {
        input.setAttribute('role', 'switch');
        input.setAttribute('aria-label', label);
        const labelEl = document.querySelector(`label[for="${id}__toggle"]`);
        if (labelEl) {
          labelEl.removeAttribute('for');
        }
      }
    });
  }, [appAnalyticsTitle, advertisingTitle, appDataTransferTitle]);

  const userConsentsAccordionItems = [
    {
      content: <ToggleLabelText dangerouslySetInnerHTML={{ __html: appAnalyticsContent }} />,
      header: {
        startArea: <Graphic id="graphic-analytics" src={svgAnalytics} alt="analytics" />,
        centralArea: <ToggleLabelHeader>{appAnalyticsTitle}</ToggleLabelHeader>,
        endArea: (
          <UiToggle
            data-testid="setAnalytics-btn"
            id="app-analytics-toggle"
            on={analytics}
            onChange={(e) => {
              setAnalytics(e);
            }}
          />
        )
      },
      id: 'app-analytics-accordion-item',
      expanded: true
    },
    {
      content: <ToggleLabelText dangerouslySetInnerHTML={{ __html: advertisingContent }} />,
      header: {
        startArea: <IconMegaphone id="graphic-advertising" alt="advertising" />,
        centralArea: <ToggleLabelHeader>{advertisingTitle}</ToggleLabelHeader>,
        endArea: (
          <UiToggle
            data-testid="setAdvertising-btn"
            id="advertising-toggle"
            on={advertising}
            onChange={(e) => {
              setAdvertising(e);
            }}
          />
        )
      },
      id: 'advertising-accordion-item',
      expanded: isDesktop
    }
  ];

  const handleFocus = (id) => {
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el && typeof el.focus === 'function') {
        el.focus();
        // Force focus for screen readers
        if (el.getAttribute('role') === 'switch') {
          el.setAttribute('tabindex', '0');
        }
      }
    }, 100);
  };

  const printerConsentsAccordionItems = [
    {
      content: <ToggleLabelText dangerouslySetInnerHTML={{ __html: defaultConsentContent }} />,
      header: {
        startArea: <IconJobs id="graphic-required" alt="required" />,
        centralArea: <ToggleLabelHeader>{appRequiredTitle}</ToggleLabelHeader>
      },
      id: 'app-required-accordion-item',
      expanded: true
    },
    ...userConsentsAccordionItems
  ];

  const wjaConsentsAccordionItems = [
    {
      content: <ToggleLabelText dangerouslySetInnerHTML={{ __html: defaultConsentContent }} />,
      header: {
        centralArea: <ToggleLabelHeader>{appRequiredTitle}</ToggleLabelHeader>
      },
      id: 'app-required-accordion-item',
      expanded: isDesktop
    },
    {
      content: (
        <WjaContainer>
          <WjaToggleHeader>
            <Graphic id="graphic-analytics" src={svgAnalytics} alt="analytics" />
            <WjaToggleHeaderTitle>{appAnalyticsTitle}</WjaToggleHeaderTitle>
          </WjaToggleHeader>
          <WjaToggleBody>
            <ToggleLabelText dangerouslySetInnerHTML={{ __html: appAnalyticsContent }} />
            <WjaToggleBodyExtens>
              <WjaToggleBodyExtensContainer>
                <WjaToggleBodyExtensContainerDesc>
                  {appOptionalDataUseDesc}
                </WjaToggleBodyExtensContainerDesc>
                <WjaToggleBodyExtensContainerButton>
                  <Toggle
                    data-testid="setAnalytics-btn"
                    id="app-analytics-toggle"
                    defaultOn={analytics}
                    onChange={(e) => {
                      setAnalytics(e);
                      setRenderPageAnyToggleClick(true);
                    }}
                  />
                  <WjaToggleBodyExtensContainerButtonText>
                    {analytics ? 'On' : 'Off'}
                  </WjaToggleBodyExtensContainerButtonText>
                </WjaToggleBodyExtensContainerButton>
              </WjaToggleBodyExtensContainer>
            </WjaToggleBodyExtens>
          </WjaToggleBody>
        </WjaContainer>
      ),
      header: {
        centralArea: <ToggleLabelHeader>{appOptionalTitle}</ToggleLabelHeader>
      },
      id: 'app-analytics-accordion-item',
      expanded: true
    }
  ];

  const piplUserConsentsAccordionItems = [
    {
      content: <ToggleLabelText dangerouslySetInnerHTML={{ __html: dataTransferContent }} />,
      header: {
        startArea: <IconShare id="graphic-data-transfer" alt="data-transfer" />,
        centralArea: <ToggleLabelHeader>{appDataTransferTitle}</ToggleLabelHeader>,
        endArea: (
          <UiToggle
            data-testid="setDataTransfer-btn"
            id="dataTransfer-toggle"
            on={dataTransfer}
            onChange={(e) => {
              setDataTransfer(e);
              setAnalytics(false);
              setAdvertising(false);
            }}
          />
        )
      },
      id: 'data-transfer-accordion-item',
      expanded: true
    },
    {
      content: <ToggleLabelText dangerouslySetInnerHTML={{ __html: appAnalyticsContent }} />,
      header: {
        startArea: <Graphic id="graphic-analytics" src={svgAnalytics} alt="analytics" />,
        centralArea: <ToggleLabelHeader>{appAnalyticsTitle}</ToggleLabelHeader>,
        endArea: (
          <Toggle
            data-testid="setAnalytics-btn"
            id="app-analytics-toggle"
            disabled={!dataTransfer}
            defaultOn={analytics}
            onChange={(e) => {
              setAnalytics(e);
            }}
          />
        )
      },
      id: 'app-analytics-accordion-item',
      expanded: true
    },
    {
      content: <ToggleLabelText dangerouslySetInnerHTML={{ __html: advertisingContent }} />,
      header: {
        startArea: <IconMegaphone id="graphic-advertising" alt="advertising" />,
        centralArea: <ToggleLabelHeader>{advertisingTitle}</ToggleLabelHeader>,
        endArea: (
          <Toggle
            data-testid="setAdvertising-btn"
            id="advertising-toggle"
            disabled={!dataTransfer}
            defaultOn={advertising}
            onChange={(e) => {
              setAdvertising(e);
            }}
          />
        )
      },
      id: 'advertising-accordion-item',
      expanded: isDesktop
    }
  ];

  const piplPrinterConsentsAccordionItems = [
    {
      content: <ToggleLabelText dangerouslySetInnerHTML={{ __html: defaultConsentContent }} />,
      header: {
        startArea: <IconJobs id="graphic-required" alt="required" />,
        centralArea: <ToggleLabelHeader>{appRequiredTitle}</ToggleLabelHeader>
      },
      id: 'app-required-accordion-item',
      expanded: true
    },
    ...piplUserConsentsAccordionItems
  ];

  const PrivacyConsents = ({ isPipl }) => {
    if (printerWelcome && isPipl) {
      return (
        <>
          <Accordion behavior={'multiExpand'} >
            <Accordion.Item expanded={true} disabled={true} >
              <Accordion.Header>
                <RebrandAccordionHeaderContent>
                  <RebrandAccordionHeaderStart>
                    <IconJobs />
                    <RebrandToggleLabelHeader>{appRequiredTitle}</RebrandToggleLabelHeader>
                  </RebrandAccordionHeaderStart>
                  <div></div>
                </RebrandAccordionHeaderContent>
              </Accordion.Header>
              <Accordion.Body>
                <RebrandToggleLabelText dangerouslySetInnerHTML={{ __html: defaultConsentContent }} />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          <Accordion behavior={'multiExpand'}>
            <Accordion.Item expanded={true}>
              <Accordion.Header>
                <RebrandAccordionHeaderContent>
                  <RebrandAccordionHeaderStart>
                    <IconShare id="graphic-data-transfer" alt="data-transfer" />
                    <RebrandToggleLabelHeader>{appDataTransferTitle}</RebrandToggleLabelHeader>
                  </RebrandAccordionHeaderStart>
                  <div>
                    <PrinterConsentsToggle
                      initialState={dataTransferRef.current}
                      onChange={handleDataTransferChange}
                    />
                  </div>
                </RebrandAccordionHeaderContent>
              </Accordion.Header>
              <Accordion.Body>
                <RebrandToggleLabelText dangerouslySetInnerHTML={{ __html: dataTransferContent }} />
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item expanded={true}>
              <Accordion.Header>
                <RebrandAccordionHeaderContent>
                  <RebrandAccordionHeaderStart>
                    <IconBarChart id="graphic-analytics" alt="analytics" />
                    <RebrandToggleLabelHeader>{appAnalyticsTitle}</RebrandToggleLabelHeader>
                  </RebrandAccordionHeaderStart>
                  <div>
                    <PrinterConsentsToggle
                      initialState={analyticsRef.current}
                      onChange={handleAnalyticsChange}
                    />
                  </div>
                </RebrandAccordionHeaderContent>
              </Accordion.Header>
              <Accordion.Body>
                <RebrandToggleLabelText dangerouslySetInnerHTML={{ __html: appAnalyticsContent }} />
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item expanded={true}>
              <Accordion.Header>
                <RebrandAccordionHeaderContent>
                  <RebrandAccordionHeaderStart>
                    <IconMegaphone id="graphic-advertising" alt="advertising" />
                    <RebrandToggleLabelHeader>{advertisingTitle}</RebrandToggleLabelHeader>
                  </RebrandAccordionHeaderStart>
                  <div>
                    <PrinterConsentsToggle
                      initialState={advertisingRef.current}
                      onChange={handleAdvertisingChange}
                    />
                  </div>
                </RebrandAccordionHeaderContent>
              </Accordion.Header>
              <Accordion.Body>
                <RebrandToggleLabelText dangerouslySetInnerHTML={{ __html: advertisingContent }} />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </>
      );
    }

    if (printerWelcome && !isPipl) {
      return (
        <>
          <Accordion behavior={'multiExpand'} >
            <Accordion.Item expanded={true} disabled={true} >
              <Accordion.Header>
                <RebrandAccordionHeaderContent>
                  <RebrandAccordionHeaderStart>
                    <IconJobs />
                    <RebrandToggleLabelHeader>{appRequiredTitle}</RebrandToggleLabelHeader>
                  </RebrandAccordionHeaderStart>
                  <div></div>
                </RebrandAccordionHeaderContent>
              </Accordion.Header>
              <Accordion.Body>
                <RebrandToggleLabelText dangerouslySetInnerHTML={{ __html: defaultConsentContent }} />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          <Accordion behavior={'multiExpand'} >
            <Accordion.Item expanded={true}>
              <Accordion.Header>
                <RebrandAccordionHeaderContent>
                  <RebrandAccordionHeaderStart>
                    <IconBarChart id="graphic-analytics" alt="analytics" />
                    <RebrandToggleLabelHeader>{appAnalyticsTitle}</RebrandToggleLabelHeader>
                  </RebrandAccordionHeaderStart>
                  <div>
                    <PrinterConsentsToggle
                      initialState={analyticsRef.current}
                      onChange={handleAnalyticsChange}
                    />
                  </div>
                </RebrandAccordionHeaderContent>
              </Accordion.Header>
              <Accordion.Body>
                <RebrandToggleLabelText dangerouslySetInnerHTML={{ __html: appAnalyticsContent }} />
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item expanded={true}>
              <Accordion.Header>
                <RebrandAccordionHeaderContent>
                  <RebrandAccordionHeaderStart>
                    <IconMegaphone id="graphic-advertising" alt="advertising" />
                    <RebrandToggleLabelHeader>{advertisingTitle}</RebrandToggleLabelHeader>
                  </RebrandAccordionHeaderStart>
                  <div>
                    <PrinterConsentsToggle
                      initialState={advertisingRef.current}
                      onChange={handleAdvertisingChange}
                    />
                  </div>
                </RebrandAccordionHeaderContent>
              </Accordion.Header>
              <Accordion.Body>
                <RebrandToggleLabelText dangerouslySetInnerHTML={{ __html: advertisingContent }} />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </>
      );
    }

    if (userConsentsUpdate) {
      return (
        <ListContainer
          items={[
            ...(isPipl
              ? [
                {
                  label: appDataTransferTitle,
                  description: (
                    <RebrandToggleLabelText
                      dangerouslySetInnerHTML={{ __html: dataTransferContent }}
                    />
                  ),
                  alignEnd: 'top',
                  paddingEnd: true,
                  paddingStart: true,
                  end: (
                    <UiToggle
                      aria-label={appDataTransferTitle}
                      data-testid="setDataTransfer-btn"
                      id="dataTransfer-toggle"
                      on={dataTransfer}
                      onChange={(e) => {
                        handleToggleChangeWithFocus('dataTransfer-toggle', setDataTransfer, e, () => {
                          setAnalytics(false);
                          setAdvertising(false);
                        });
                      }}
                    />
                  )
                }
              ]
              : []),
            {
              label: appAnalyticsTitle,
              description: (
                <RebrandToggleLabelText dangerouslySetInnerHTML={{ __html: appAnalyticsContent }} />
              ),
              alignEnd: 'top',
              paddingEnd: true,
              paddingStart: true,
              end: (
                <UiToggle
                  data-testid="setAnalytics-btn"
                  id="app-analytics-toggle"
                  on={analytics}
                  disabled={isPipl ? !dataTransfer : false}
                  onChange={(e) => {
                    handleToggleChangeWithFocus('app-analytics-toggle', setAnalytics, e);
                  }}
                />
              )
            },
            {
              label: advertisingTitle,
              description: (
                <RebrandToggleLabelText dangerouslySetInnerHTML={{ __html: advertisingContent }} />
              ),
              alignEnd: 'top',
              paddingEnd: true,
              paddingStart: true,
              end: (
                <UiToggle
                  data-testid="setAdvertising-btn"
                  id="advertising-toggle"
                  on={advertising}
                  disabled={isPipl ? !dataTransfer : false}
                  onChange={(e) => {
                    handleToggleChangeWithFocus('advertising-toggle', setAdvertising, e);
                  }}
                />
              )
            }
          ]}
        />
      );
    }

    return (
      <StyledAccordion
        behavior={isDesktop ? 'multiExpand' : 'singleExpand'}
        items={
          hasAppNameWJA
            ? wjaConsentsAccordionItems
            : !isPipl
              ? userConsentsAccordionItems
              : piplUserConsentsAccordionItems
        }
        isWJA={hasAppNameWJA}
        id="privacy-consents-items"
      />
    );
  };

  const EasyStartPrivacyConsents = () => {
    return (
      <React.Fragment>
        <StyledAccordion
          behavior={isDesktop ? 'singleExpand' : 'singleExpand'}
          items={[
            {
              content: (
                <ToggleLabelText dangerouslySetInnerHTML={{ __html: easyStartBasicContent }} />
              ),
              header: {
                startArea: <IconShield />,
                centralArea: <ToggleLabelHeader>{easyStartRequiredText}</ToggleLabelHeader>,
                endArea: (
                  <Toggle
                    id="basic-easystart-toggle"
                    data-testid="basic-easystart-toggle"
                    defaultOn
                    disabled
                    onChange={(e) => {
                      seteasyStartBasic(e);
                    }}
                  />
                )
              },
              id: 'basic-easystart-accordion-item',
              expanded: true,
              disabled: true
            }
          ]}
          id="basic-easystart-items"
        />
        <StyledAccordion
          behavior={isDesktop ? 'singleExpand' : 'singleExpand'}
          items={[
            {
              content: (
                <ToggleLabelText dangerouslySetInnerHTML={{ __html: easyStartUsageContent }} />
              ),
              header: {
                startArea: <Graphic id="usage-analytics" src={svgAnalytics} alt="analytics" />,
                centralArea: <ToggleLabelHeader>{easyStartAnalyticsText}</ToggleLabelHeader>,
                endArea: (
                  <Toggle
                    id="easystart-analytics-toggle"
                    data-testid="easystart-analytics-toggle"
                    defaultOn={easyStartUsage}
                    onChange={(e) => {
                      seteasyStartUsage(e);
                    }}
                  />
                )
              },
              id: 'easystart-analytics-accordion-item',
              expanded: true
            }
          ]}
          id="easystart-consents-items"
        />
      </React.Fragment>
    );
  };

  const EasyStartProPrivacyConsents = () => {
    return (
      <React.Fragment>
        <StyledAccordion
          behavior={isDesktop ? 'singleExpand' : 'singleExpand'}
          items={[
            {
              content: (
                <ToggleLabelText
                  dangerouslySetInnerHTML={{ __html: easyStartProOperationContent }}
                />
              ),
              header: {
                startArea: <IconShield />,
                centralArea: <ToggleLabelHeader>{easyStartProRequiredText}</ToggleLabelHeader>,
                endArea: (
                  <Toggle
                    id="basic-easystartpro-toggle"
                    data-testid="basic-easystartpro-toggle"
                    defaultOn
                    disabled
                    onChange={(e) => {
                      seteasyStartProOperation(e);
                    }}
                  />
                )
              },
              id: 'basic-easystartpro-accordion-item',
              expanded: true,
              disabled: true
            }
          ]}
          id="basic-easystartpro-items"
        />
        <StyledAccordion
          behavior={isDesktop ? 'singleExpand' : 'singleExpand'}
          items={[
            {
              content: (
                <ToggleLabelText dangerouslySetInnerHTML={{ __html: easyStartProUsageContent }} />
              ),
              header: {
                startArea: <Graphic id="usage-analytics" src={svgAnalytics} alt="analytics" />,
                centralArea: <ToggleLabelHeader>{easyStartProAnalyticsText}</ToggleLabelHeader>,
                endArea: (
                  <Toggle
                    id="easystartpro-analytics-toggle"
                    data-testid="easystartpro-analytics-toggle"
                    defaultOn={easyStartProUsage}
                    onChange={(e) => {
                      seteasyStartProUsage(e);
                    }}
                  />
                )
              },
              id: 'easystartpro-analytics-accordion-item',
              expanded: true
            }
          ]}
          id="easystartpro-consents-items"
        />
      </React.Fragment>
    );
  };

  if (userConsentsUpdate) {
    const getButtons = () => {
      if (isLargeScreen) {
        return (
          <RebrandUserConsentsUpdateButtonLargeScreen>
            {isShowBackButton && (
              <Button
                onClick={() => {
                  backClick();
                  sendSimpleUiEvent(backButtonClickTrackEvent);
                }}
                size="medium"
                type="secondary"
                id="consent-back-button"
              >
                {backButton}
              </Button>
            )}
            <Button
              onClick={() => {
                handleClick();
                continueClickBatchEvent();
              }}
              disabled={hasAppNameWJA ? !isEnableContinueButton : loading}
              size="medium"
              type="primary"
              id="consent-continue-button"
            >
              {loading ? <ButtonLoaderWrapper><Loader type="circular" variant="small" /></ButtonLoaderWrapper> : (onBoard ? continueButton : saveButton)}
            </Button>
          </RebrandUserConsentsUpdateButtonLargeScreen>
        );
      }
      return (
        <>
          <Button
            onClick={() => {
              handleClick();
              continueClickBatchEvent();
            }}
            disabled={hasAppNameWJA ? !isEnableContinueButton : loading}
            size="medium"
            type="primary"
            id="consent-continue-button"
          >
            {loading ? <ButtonLoaderWrapper><Loader type="circular" variant="small" /></ButtonLoaderWrapper> : (onBoard ? continueButton : saveButton)}
          </Button>
          {isShowBackButton && (
            <Button
              onClick={() => {
                backClick();
                sendSimpleUiEvent(backButtonClickTrackEvent);
              }}
              size="medium"
              type="secondary"
              id="consent-back-button"
            >
              {backButton}
            </Button>
          )}
        </>
      );
    };

    return (
      <RebrandEmptyPageWrapper>
        <EmptyPage>
          <RebrandEmptyPageContentWrapper>
            <div
              role="status"
              aria-live="polite"
              aria-atomic="true"
              tabIndex="-1"
              style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}
            >
              {a11yAnnouncement}
            </div>
            <RebrandUserConsentsTitle isRTL={isRTL} id="title-text" isDesktop={isDesktop}>
              {title}
            </RebrandUserConsentsTitle>
            <RebrandUserConsentsBody
              id="settings-container-body"
              isDesktop={isDesktop}
              isWJA={hasAppNameWJA}
              dangerouslySetInnerHTML={{ __html: body }}
              isRTL={isRTL}
            />
            <GenericCard
              id="settings-container"
              data-testid="privacy-settings"
              className="privacy-settings-card"
            >
              <GenericCard.Body>
                <RebrandUserConsentsCardContent isRTL={isRTL}>
                  {PrivacyConsents({ isPipl })}
                </RebrandUserConsentsCardContent>
              </GenericCard.Body>
            </GenericCard>
            <RebrandUserConsentsBody
              isDesktop={isDesktop}
              isWJA={hasAppNameWJA}
              dangerouslySetInnerHTML={{ __html: footer }}
              isRTL={isRTL}
            />
            <RebrandUserConsentsUpdateButtonContainer
              ref={buttonsContainerRef}
              id="consents-button-container"
              isShowBackButton={isShowBackButton}
              isRTL={isRTL}
            >
              {getButtons()}
            </RebrandUserConsentsUpdateButtonContainer>
          </RebrandEmptyPageContentWrapper>
        </EmptyPage>
      </RebrandEmptyPageWrapper>
    );
  }

  const RebrandPrivacyToggles = () => {
    const items = [
      {
        label: <ListContainerTitleRebrand>{appRequiredTitle}</ListContainerTitleRebrand>,
        secondaryText: <ListContainerRebrand>{defaultConsentContent}</ListContainerRebrand>,
        paddingStart: true,
        paddingEnd: true,
        alignEnd: 'top'
      },
      ...(isPipl
        ? [
          {
            label: <ListContainerTitleRebrand><span id="printer-data-transfer-label">{appDataTransferTitle}</span></ListContainerTitleRebrand>,
            secondaryText: <ListContainerRebrand>{dataTransferContent}</ListContainerRebrand>,
            alignEnd: 'top',
            end: (
              <Toggle
                id="app-data-transfer-toggle"
                on={dataTransfer}
                onChange={(e) => {
                  handleToggleChangeWithFocus(
                    'app-data-transfer-toggle',
                    setDataTransfer,
                    e,
                    () => {
                      setAnalytics(false);
                      setAdvertising(false);
                    }
                  );
                }}
              />
            ),
            alignEnd: 'top',
            secondaryText: <ListContainerRebrand>{dataTransferContent}</ListContainerRebrand>,
            paddingStart: true,
            paddingEnd: true
          }
        ]
        : []),
      {
        label: <ListContainerTitleRebrand><span id="printer-analytics-label">{appAnalyticsTitle}</span></ListContainerTitleRebrand>,
        end: (
          <Toggle
            id="app-analytics-toggle"
            on={analytics}
            disabled={isPipl ? !dataTransfer : false}
            onChange={(e) => {
              handleToggleChangeWithFocus('app-analytics-toggle', setAnalytics, e);
            }}
          />
        ),
        alignEnd: 'top',
        secondaryText: (
          <ListContainerRebrand disabled={isPipl ? !dataTransfer : false}>
            {appAnalyticsContent}
          </ListContainerRebrand>
        ),
        disabled: isPipl ? !dataTransfer : false,
        paddingStart: true,
        paddingEnd: true
      },
      {
        label: <ListContainerTitleRebrand><span id="printer-advertising-label">{advertisingTitle}</span></ListContainerTitleRebrand>,
        end: (
          <Toggle
            id="app-advertising-toggle"
            on={advertising}
            disabled={isPipl ? !dataTransfer : false}
            onChange={(e) => {
              handleToggleChangeWithFocus('app-advertising-toggle', setAdvertising, e);
            }}
          />
        ),
        alignEnd: 'top',
        secondaryText: (
          <ListContainerRebrand disabled={isPipl ? !dataTransfer : false}>
            <span dangerouslySetInnerHTML={{ __html: advertisingContent }} />
          </ListContainerRebrand>
        ),
        disabled: isPipl ? !dataTransfer : false,
        paddingStart: true,
        paddingEnd: true
      }
    ];

    return <ListContainer items={items}></ListContainer>
  }
  if (printerConsentsUpdate) {
    const getButtons = () => {
      if (isLargeScreen) {
        return (
          <ConsentsButtonLargeContainer>
            {isShowBackButton && (
              <Button
                onClick={() => {
                  backClick();
                  sendSimpleUiEvent(backButtonClickTrackEvent);
                }}
                size="medium"
                type="ghost-flush"
                id="consent-back-button"
              >
                {backButton}
              </Button>
            )}
            <Button
              onClick={handleClick}
              disabled={hasAppNameWJA ? !isEnableContinueButton : loading}
              size="medium"
              type="primary"
              id="consent-continue-button"
            >
              {loading ? <ButtonLoaderWrapper><Loader type="circular" variant="small" /></ButtonLoaderWrapper> : (onBoard ? continueButton : saveButton)}
            </Button>
          </ConsentsButtonLargeContainer>
        );
      }
      return (
        <>
          <Button
            onClick={handleClick}
            disabled={hasAppNameWJA ? !isEnableContinueButton : loading}
            size="medium"
            type="primary"
            id="consent-continue-button"
          >
            {loading ? <ButtonLoaderWrapper><Loader type="circular" variant="small" /></ButtonLoaderWrapper> : (onBoard ? continueButton : saveButton)}
          </Button>
          {isShowBackButton && (
            <Button
              onClick={() => {
                backClick();
                sendSimpleUiEvent(backButtonClickTrackEvent);
              }}
              size="medium"
              type="ghost-flush"
              id="consent-back-button"
            >
              {backButton}
            </Button>
          )}
        </>
      );
    };

    return (
      <RebrandBackGround isRTL={isRTL}>
        <DeviceListPage>
          <GenericCard
            id="settings-container"
            data-testid="privacy-settings"
            className="privacy-settings-card"
          >
            <GenericCard.Header title={title} borderBottom={false} />
            <GenericCard.Body paddingBodyBottom={true}>
              <PrinterConsentDescription
                dangerouslySetInnerHTML={{ __html: body }}
                className="body-regular"
              />
              {RebrandPrivacyToggles()}
              <ConsentsButtonContainer
                id="consents-button-container"
                isWJA={hasAppNameWJA}
                isDesktop={isDesktop}
              >
                {getButtons()}
              </ConsentsButtonContainer>
            </GenericCard.Body>
          </GenericCard>
        </DeviceListPage>
      </RebrandBackGround>
    );
  }

  return (
    <ReTargetContainer
      id="settings-container"
      data-testid="privacy-settings"
      isDesktop={isDesktop}
      isRTL={isRTL}
    >
      <ReTargetContentContainer id="content-container" isIOS={isIOS}>
        <Title
          id="settings-container-title"
          isDesktop={isDesktop}
          dangerouslySetInnerHTML={{ __html: title }}
          isRTL={isRTL}
        />
        <Body
          id="settings-container-body"
          isDesktop={isDesktop}
          isWJA={hasAppNameWJA}
          dangerouslySetInnerHTML={{ __html: body }}
          isRTL={isRTL}
        />
        <ToggleLayout id="settings-container-toggle-layout" isDesktop={isDesktop}>
          {(() => {
            if (hasAppNameEasyStart) {
              return <EasyStartPrivacyConsents isDesktop={isDesktop} />;
            } else if (hasAppNameEasyStartPro) {
              return <EasyStartProPrivacyConsents isDesktop={isDesktop} />;
            } else {
              return <PrivacyConsents isDesktop={isDesktop} isPipl={isPipl} />;
            }
          })()}
        </ToggleLayout>
        {userConsents ? (
          footer && footer.match(/<[a-z]+>/) ? (
            <Footer
              isDesktop={isDesktop}
              dangerouslySetInnerHTML={{ __html: footer }}
              isRTL={isRTL}
            />
          ) : (
            footer
          )
        ) : (
          ''
        )}
      </ReTargetContentContainer>
      <ConsentsButtonContainer
        id="consents-button-container"
        isWJA={hasAppNameWJA}
        isDesktop={isDesktop}
      >
        <Button
          onClick={() => {
            handleClick();
            sendSimpleUiEvent(continueButtonClickTrackEvent);
          }}
          disabled={hasAppNameWJA ? !isEnableContinueButton : loading}
          size="medium"
          type="primary"
          id="consent-continue-button"
        >
          {loading ? <ButtonLoaderWrapper><Loader type="circular" variant="small" /></ButtonLoaderWrapper> : (onBoard ? continueButton : saveButton)}
        </Button>
        {isShowBackButton && (
          <Button
            onClick={() => {
              backClick();
              sendSimpleUiEvent(backButtonClickTrackEvent);
            }}
            size="medium"
            type="ghost"
            id="consent-back-button"
          >
            {backButton}
          </Button>
        )}
      </ConsentsButtonContainer>
    </ReTargetContainer>
  );
};

export default PrivacySettingsRebrand;

//temporary text before changing it in alpacadata
// const newMarketingText = `<p>See relevant ad content from HP, based on app feature use. Some data will be shared with select marketing partners and platforms to ensure that you are kept up to date with the latest offers from HP. <a href="https://www.hpsmart.com/plain/data-sharing-notice" target="_blank">Learn more</a></p>`;
